import { z } from 'zod';
import { TFunction } from 'i18next';
import { ProjectExpenseFrequency } from '@/models/common/ProjectStatus';

/** Sentinel for the "אחר" card in step 1 / the "אחר" option in a supplier's activity domain. */
export const OTHER_OPTION_ID = -1;

const expenseSchema = z.object({
  // Optional so the input can start EMPTY instead of showing a 0 the coordinator must delete.
  // An unfilled row is dropped before the POST; see mapFormDataToDto.
  amount: z.number().optional(),
  // No "reason" field: the office asked for it to be dropped (HAT-1105, 22/10/2026). The column
  // stays on the server for the rows written before that, and the DTO sends it as null.
  frequency: z.enum(ProjectExpenseFrequency).default(ProjectExpenseFrequency.Monthly),
  validUntil: z.string().nullable().default(null),
});

const supplierSchema = z.object({
  /** Set when an existing supplier of the fund was picked; null for a newly typed one. */
  supplierId: z.number().nullable().default(null),
  name: z.string().default(''),
  taxId: z.string().default(''),
  /** OTHER_OPTION_ID means the free-text domain below is used instead of the closed list. */
  activityDomainId: z.number().nullable().default(null),
  activityDomainOther: z.string().default(''),
});

const documentSchema = z.object({
  file: z.custom<File | null>(),
  description: z.string().default(''),
});

/**
 * Schema of the "פרויקט חדש" wizard. Per-step validation lives in the step components
 * (they call trigger() on their own fields), so most rules here are shape-only; the
 * cross-field rules that a single field cannot express are the refinements below.
 */
export const createProjectFormSchema = (t: TFunction) => z.object({
  // Step 1 - the name moved here from step 3 (HAT-1105, 22/10/2026): it is the first thing the
  // office asks about a project, so it is the first thing the coordinator is asked for.
  name: z.string().default(''),
  projectTypeId: z.number().nullable().default(null),
  projectTypeOther: z.string().default(''),
  description: z.string().default(''),
  // Step 2
  expenses: z.array(expenseSchema).default([]),
  suppliers: z.array(supplierSchema).default([]),
  documents: z.array(documentSchema).default([]),
  // Step 3
  userComments: z.string().default(''),
  legalTermsApproved: z.boolean().default(false),
})
  .refine(
    (data) => data.name.trim().length > 0,
    { message: t('projects.wizard.validation.nameRequired'), path: ['name'] }
  )
  .refine(
    (data) => data.projectTypeId !== null,
    { message: t('projects.wizard.validation.projectTypeRequired'), path: ['projectTypeId'] }
  )
  .refine(
    (data) => data.projectTypeId !== OTHER_OPTION_ID || data.projectTypeOther.trim().length > 0,
    { message: t('projects.wizard.validation.otherTypeRequired'), path: ['projectTypeOther'] }
  )
  // The description is mandatory (the office marked it so on 22/10/2026): it is what the
  // committee reads to decide, so a project without one cannot be assessed.
  .refine(
    (data) => data.description.trim().length > 0,
    { message: t('projects.wizard.validation.descriptionRequired'), path: ['description'] }
  )
  // Step 2 carries no "at least one expense" rule: the office decided on 2026-09-17 that a
  // project may be opened without its budget and completed later, so an untouched step 2 is a
  // valid submission (its blank rows are dropped in mapFormDataToDto).
  .refine(
    (data) => data.legalTermsApproved,
    { message: t('projects.wizard.validation.legalRequired'), path: ['legalTermsApproved'] }
  );

export type ProjectFormData = z.input<ReturnType<typeof createProjectFormSchema>>;

export const emptyExpense = {
  amount: undefined,
  frequency: ProjectExpenseFrequency.Monthly,
  validUntil: null,
};

export const emptySupplier = {
  supplierId: null,
  name: '',
  taxId: '',
  activityDomainId: null,
  activityDomainOther: '',
};

export const defaultProjectFormValues: ProjectFormData = {
  projectTypeId: null,
  projectTypeOther: '',
  description: '',
  // The wizard opens step 2 with one empty expense block, as in the design.
  expenses: [{ ...emptyExpense }],
  suppliers: [],
  documents: [],
  name: '',
  userComments: '',
  legalTermsApproved: false,
};
